'use client';

import { FunctionComponent } from 'react';
import { t } from '@xFrame4/common/Utils';
import ALink from '@xFrame4/components/next/ALink';
import AppTracker from '@components/tracking/AppTracker';

export interface ContactInfoBoxProps
{
    /** The language of the box. */
    language: string;
    /** The contact information to display. */
    contactInfo: ContactInfoBoxItem;
    /** Whether to display the contact info name. */
    displayContactInfoName?: boolean;
    /** Additional class name of the box. */
    className?: string;
}

/**
 * Component: a box that displays contact information.
 */
const ContactInfoBox: FunctionComponent<ContactInfoBoxProps> = (props) =>
{
    const displayContactInfoName = props.displayContactInfoName ?? false;

    /** Render */
    return (
        <div className={`contact-info-box bg-primary-light text-white ${props.className ?? ''}`}>
            <div className="contact-info-title w-100 text-uppercase text-center font-size-3 fw-bold bg-secondary-light px-3 pt-2 pb-1">
                {props.contactInfo.title ?? t('CONTACT_US', props.language)}
            </div>

            <div className="contact-info-box-content text-center px-3 py-2">
                {displayContactInfoName && props.contactInfo.name && props.contactInfo.name.length > 0 &&
                    <div className="contact-info-box-name mb-1">
                        <i className="fa fa-user me-2"></i>
                        {props.contactInfo.name}
                    </div>
                }
                {props.contactInfo.email && props.contactInfo.email.length > 0 &&
                    <div className="contact-info-box-email mb-2 mb-lg-1">
                        <span className="font-weight-500 me-2">{t('EMAIL', props.language)}:</span>
                        <ALink
                            href={`mailto:${props.contactInfo.email}`}
                            className="text-white text-decoration-none"
                            onClick={() => AppTracker.trackEmailClick()}
                        >
                            {props.contactInfo.email}
                        </ALink>
                    </div>
                }
                {props.contactInfo.phone && props.contactInfo.phone.length > 0 &&
                    <div className="contact-info-box-phone mb-2 mb-lg-1">
                        <span className="font-weight-500 me-2">{t('PHONE', props.language)}:</span>
                        <ALink
                            href={`tel:${props.contactInfo.phone}`}
                            className="text-white text-decoration-none"
                            onClick={() => AppTracker.trackPhoneClick()}
                        >
                            {props.contactInfo.phone}
                        </ALink>
                    </div>
                }
            </div>
        </div>
    );
}

export type ContactInfoBoxItem = {
    /** The title of the box. Eg: "Contact us" */
    title?: string;
    /** The name of the contact. Eg: "John Doe" */
    name?: string;
    /** The email address of the contact. Eg: "john.doe@example.com" */
    email?: string;
    /** The phone number of the contact. Eg: "+1234567890" */
    phone?: string;
}

export default ContactInfoBox;